/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { useId, useState } from 'react';

import { cssMap, cx, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import SearchIcon from '@atlaskit/icon/core/search';
import Modal, { CloseButton, ModalBody, ModalTransition } from '@atlaskit/modal-dialog';
import { ExitingPersistence } from '@atlaskit/motion';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { ButtonMenuItem } from '@atlaskit/side-nav-items/button-menu-item';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import TextField from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';

import { SubScreenOverlay } from './sub-screen-overlay';
import { useSubscreenStack } from './useSubscreenStack';

const styles = cssMap({
	wrapper: {
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
		height: '100%',
		// Match the legacy ADS Modal radius. Inner clipping needs to align with whatever
		// radius the outer @atlaskit/modal-dialog applies, otherwise the scrollbar (or any
		// other overflow content) won't align with the modal's rounded corners.
		// See `wrapperT26` for the new T26 shape theme path.
		borderRadius: token('radius.small', '3px'),
	},
	wrapperT26: {
		// platform-dst-shape-theme-default: T26 shape theme uses a larger radius on the
		// outer modal-dialog. Keep this in sync with the same gate in @atlaskit/modal-dialog
		// (see modal-dialog.tsx → `dialogStyles.borderRadiusT26Shape`).
		borderRadius: token('radius.xlarge', '12px'),
	},
	container: {
		display: 'flex',
		flexDirection: 'row',
		flex: '1 1 0%',
		minHeight: '0px',
		overflow: 'hidden',
	},
	sideNav: {
		width: '225px',
		minWidth: '225px',
		flexShrink: 0,
		alignSelf: 'stretch',
		alignItems: 'flex-start',
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		gap: token('space.200'),
		display: 'flex',
		flexDirection: 'column',
	},
	searchBoxWrapper: {
		width: '100%',
		boxSizing: 'border-box',
		paddingBlock: token('space.075'),
		paddingInline: token('space.100'),
		marginBlockEnd: token('space.100'),
		borderRadius: token('radius.small'),
		border: `${token('border.width.selected')} solid ${token('color.border.selected')}`,
		backgroundColor: token('color.background.input.pressed'),
	},
	searchIconWrapper: {
		paddingBlockStart: token('space.025'),
		paddingInlineStart: token('space.100'),
	},
	navList: {
		flex: 1,
		overflowY: 'auto',
		width: '100%',
	},
	mainPane: {
		flex: '1 1 0%',
		minWidth: '0px',
		minHeight: '0px',
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
	},
	content: {
		minWidth: '0px',
		minHeight: '0px',
		width: '100%',
		maxWidth: '100%',
		height: '100%',
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
	},
	bodyHeaderRow: {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
	},
	bodyContent: {
		flex: '1 1 0%',
		minWidth: '0px',
		minHeight: '0px',
		overflowY: 'auto',
		paddingBottom: token('space.200'),
	},
});

/** Describes a sub-screen that can be pushed on top of the main modal content. */
export type SubScreen = {
	/** Plain-text label shown next to the back-arrow, e.g. "Back to Rovo memory" */
	backLabel: string;
	/** The content to render in the subscreen body */
	content: React.ReactNode;
};

export type SubScreenActions = {
	/** Push a new subscreen onto the stack, hiding the sidebar and showing the automatic SubScreenHeader. */
	pushSubscreen: (subscreen: SubScreen) => void;
	/** Pop the current subscreen and return to the previous view. */
	popSubscreen: () => void;
	/** True when a subscreen is currently active. */
	hasSubscreen: boolean;
};

export type SideNavModalItem = {
	id: string;
	label: React.ReactNode;
	/** Plain-text string used for search filtering when `label` is a React component. Falls back to `label` if it is a string. */
	searchLabel?: string;
	content: (subscreen: SubScreenActions) => React.ReactNode;
	icon?: React.ReactNode;
	/** Header rendered above the active item's content area. Overrides the modal-level `contentHeader` when set. */
	contentHeader?: React.ReactNode;
};

export type SideNavModalProps = {
	isOpen: boolean;
	onClose: () => void;
	title: React.ReactNode;
	items: SideNavModalItem[];
	defaultSelectedId?: string;
	navAriaLabel?: string;
	searchPlaceholder?: string;
	testId?: string;
	/** Called when the user selects a different side-nav tab. */
	onActiveTabChange?: (tabId: string) => void;
};

const CustomHeader = ({
	contentHeader,
	onClose,
	titleId,
}: {
	contentHeader?: React.ReactNode;
	onClose: () => void;
	titleId: string;
}) => {
	return (
		<Box xcss={styles.bodyHeaderRow}>
			<Heading as="h1" size="large" id={titleId}>
				{contentHeader}
			</Heading>
			<CloseButton onClick={onClose} />
		</Box>
	);
};

export const SideNavModal = ({
	isOpen,
	onClose,
	title,
	items,
	defaultSelectedId,
	navAriaLabel,
	searchPlaceholder,
	testId,
	onActiveTabChange,
}: SideNavModalProps): React.JSX.Element | null => {
	const titleId = useId();
	const [selectedNavId, setSelectedNavId] = useState<string>(
		defaultSelectedId ?? items[0]?.id ?? '',
	);
	const [searchQuery, setSearchQuery] = useState('');
	const { activeSubscreen, activeSubscreenKey, subscreenActions } = useSubscreenStack(isOpen);

	if (!isOpen) {
		return null;
	}

	if (items.length === 0) {
		return null;
	}

	const query = searchQuery.trim().toLowerCase();
	const filteredItems =
		query === ''
			? items
			: items.filter((item) => {
					const searchable = item.searchLabel ?? (typeof item.label === 'string' ? item.label : '');
					return searchable.toLowerCase().includes(query);
				});

	const effectiveSelectedId = filteredItems.some((item) => item.id === selectedNavId)
		? selectedNavId
		: (filteredItems[0]?.id ?? '');

	const selectedItem = items.find((item) => item.id === effectiveSelectedId);

	return (
		<ModalTransition>
			{isOpen && (
				<Modal
					width="x-large"
					shouldCloseOnEscapePress
					shouldCloseOnOverlayClick
					onClose={onClose}
					aria-labelledby={titleId}
					height={640}
					testId={testId}
				>
					<Box
						xcss={cx(
							styles.wrapper,
							// Mirror @atlaskit/modal-dialog's gate so our inner clipping radius
							// stays in sync with the outer modal's actual rendered corner radius.
							fg('platform-dst-shape-theme-default') && styles.wrapperT26,
						)}
					>
						<Box xcss={styles.container}>
							<Box
								xcss={styles.sideNav}
								as="nav"
								aria-label={navAriaLabel ?? (typeof title === 'string' ? title : undefined)}
							>
								{/* TODO: Add search input when the items in nav list grow more than 2 */}
								{searchPlaceholder !== undefined && (
									<Box xcss={styles.searchBoxWrapper}>
										<TextField
											appearance="none"
											isCompact
											type="search"
											value={searchQuery}
											onChange={(e) => setSearchQuery(e.currentTarget.value)}
											placeholder={searchPlaceholder}
											aria-label={searchPlaceholder}
											elemBeforeInput={
												<Box xcss={styles.searchIconWrapper}>
													<SearchIcon spacing="spacious" color={token('color.icon')} label="" />
												</Box>
											}
										/>
									</Box>
								)}
								<Box xcss={styles.navList}>
									<MenuList>
										<Stack space="space.025">
											{filteredItems.map((item) => (
												<ButtonMenuItem
													key={item.id}
													onClick={() => {
														setSelectedNavId(item.id);
														onActiveTabChange?.(item.id);
													}}
													elemBefore={item.icon}
													isSelected={effectiveSelectedId === item.id}
												>
													{typeof item.label === 'string' ? (
														<Text
															size="medium"
															weight="medium"
															color={
																effectiveSelectedId === item.id ? 'color.text.selected' : undefined
															}
														>
															{item.label}
														</Text>
													) : (
														item.label
													)}
												</ButtonMenuItem>
											))}
										</Stack>
									</MenuList>
								</Box>
							</Box>
							<Box xcss={styles.mainPane}>
								<ModalBody>
									<Box xcss={styles.content}>
										<CustomHeader
											contentHeader={selectedItem?.contentHeader}
											onClose={onClose}
											titleId={titleId}
										/>
										<Box xcss={styles.bodyContent}>
											{selectedItem?.content(subscreenActions) ?? null}
										</Box>
									</Box>
								</ModalBody>
							</Box>
						</Box>
						<ExitingPersistence appear>
							{activeSubscreen !== null && (
								<SubScreenOverlay
									key={activeSubscreenKey}
									subscreen={activeSubscreen}
									onBack={subscreenActions.popSubscreen}
									onClose={onClose}
								/>
							)}
						</ExitingPersistence>
					</Box>
				</Modal>
			)}
		</ModalTransition>
	);
};
